import * as React from 'react'
import { ApolloConsumer, Query } from 'react-apollo'
import uuid from 'uuid/v1'

import Loader from 'main/components/common/Loader'
import { CredentialsFields } from 'main/gql/credentials'
import { User, USER_QUERY, User_user } from 'main/gql/user'
import { Modal } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'
import Content from './Content'
import { track } from './track'
import { Step } from './types'

interface IProps {
  initialStep?: Step
  onAuth?: (credentials: CredentialsFields) => void
  onRequestClose: () => void
  isOnboarding?: boolean
}

export default function AuthModal(props: IProps) {
  const { initialStep = 'login', isOnboarding, onAuth, onRequestClose } = props

  const [step, setStep] = React.useState<Step>(initialStep)
  const [forgotPassword, setForgotPassword] = React.useState(false)

  const [sessionId] = React.useState(uuid())
  const trackData = { sessionId }
  React.useEffect(() => track('opened', trackData), [])

  return (
    <ApolloConsumer>
      {client => (
        <Query<User> query={USER_QUERY}>
          {({ data, loading, error }) => {
            if (loading || error || !data) {
              return <LoadingModal onRequestClose={handleRequestClose} />
            }

            return (
              <Modal
                title={forgotPassword ? 'Forgot password' : title(step, data.user)}
                onRequestClose={handleRequestClose}
                canClose={!isOnboarding}
              >
                <div>
                  <Content
                    client={client}
                    trackData={trackData}
                    user={data.user}
                    step={step}
                    onAuth={onAuth || (() => undefined)}
                    onChangeStep={setStep}
                    onForgotPassword={() => setForgotPassword(true)}
                    onRequestClose={handleRequestClose}
                    isOnboarding={isOnboarding}
                  />
                </div>
              </Modal>
            )
          }}
        </Query>
      )}
    </ApolloConsumer>
  )

  function handleRequestClose() {
    track('closed', { sessionId })
    onRequestClose()
  }
}

function LoadingModal(props: { onRequestClose: () => void }) {
  const { onRequestClose } = props

  return (
    <Modal title="" onRequestClose={onRequestClose}>
      <Loader />
    </Modal>
  )
}

function title(step: Step, user: User_user | null) {
  switch (step) {
    case 'choose_role':
      return 'I am a..'
    case 'director':
      return 'Start a Wonderschool'
    case 'login':
      return 'Welcome back'
    case 'parent':
      return "Create a parent account, it's free!"
    case 'parent_onboarding':
      return `Almost done${user?.firstName ? `, ${user.firstName}` : ''}!`
    case 'parent_intermediate':
      return `Welcome${user?.firstName ? `, ${user.firstName}` : ''}!`
    case 'parent_intermediate_existing':
      return `Welcome back${user?.firstName ? `, ${user.firstName}` : ''}!`
    case 'parent_final':
      return 'Just one last step'
    default:
      throw new UnreachableCaseError(step)
  }
}
